import { Form, FormInstance, notification } from 'antd'
import { useCallback, useState } from 'react'
import ProposalService from '../../../../../../services/ProposalService'
import * as Sentry from '@sentry/react'
import { useSelector } from 'react-redux'
import { selectUserInfo } from '../../../../../../store/user-info/selectors'
import { useIntl } from 'react-intl'
import { useAtom } from '@reatom/react'
import { orderFilesAtom } from '../../proposal-files/order-files.atom'
import { OrderFile } from '../../../../../../services'

export type FormType = {
  proposalName: string;
  proposalMessage: string;
  proposalFiles: string;
}

type ReturnType = {
  form: FormInstance<FormType>;
  isLoading: boolean;
  isSubmitDisabled: boolean;
  onFinish: (formValues: FormType) => Promise<void>;
}

export function useExternalSourceProposal(): ReturnType {
  const intl = useIntl()
  const [form] = Form.useForm()
  const [isLoading, setLoading] = useState<boolean>(false)
  const [, { setAttachedFilesMap }] = useAtom(orderFilesAtom)
  const userInfo = useSelector(selectUserInfo)
  const userId = userInfo?.user_id
  const isSubmitDisabled = !userId

  const onFinish = useCallback(async (formValues: FormType) => {
    if (!userId) {
      return
    }

    setLoading(true)

    try {
      await ProposalService
        .addProposalFromExternalSource({
          name: formValues.proposalName,
          message: formValues.proposalMessage,
          client: userId,
          urls: formValues.proposalFiles.split(','),
        })

      notification.success({
        message: intl.messages['externalSourceProposal.proposalMessage.successfulSubmit'] as string,
        duration: 5,
      })

      form.resetFields()
      setAttachedFilesMap(new Map<string, OrderFile>())
    }
    catch (error) {
      notification.error({
        message: intl.messages['externalSourceProposal.proposalMessage.errorSubmit'] as string,
        duration: 5,
      })
      Sentry.captureException(error)
    }
    finally {
      setLoading(false)
    }
  }, [intl.messages, userId])

  return {
    form,
    isLoading,
    isSubmitDisabled,
    onFinish,
  }
}
